import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  X, ShieldCheck, Storefront, Buildings, ClockCounterClockwise, Plus, Trash, PencilSimple,
  CaretDown, Sparkle, SignOut, Prohibit, ArrowCounterClockwise, UserGear, Check,
} from "@phosphor-icons/react";

const inputCls = "w-full h-10 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const STATUS_COLORS = { active: "#DCFCE7", invited: "#E0F2FE", temporarily_disabled: "#FEF9C3", suspended: "#FEE2E2", locked: "#FEE2E2", terminated: "#FECACA", archived: "#F3F4F6" };
const STATUS_OPTS = ["active", "invited", "temporarily_disabled", "suspended", "locked", "terminated", "archived"];

function PermEditor({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/employees/${userId}/permissions`).then((r) => setData(r.data)); }, [userId]);
  const setVal = async (key, value) => {
    try { await api.put(`/employees/${userId}/permissions`, { permission_key: key, value }); const r = await api.get(`/employees/${userId}/permissions`); setData(r.data); toast.success("Updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return <div className="p-4 text-sm">Loading…</div>;
  const byCat = {};
  data.permissions.forEach((p) => { (byCat[p.category] = byCat[p.category] || []).push(p); });
  return (
    <div className="fixed inset-0 z-[85] flex justify-end bg-black/40" onClick={onClose}>
      <div data-testid="perm-editor" onClick={(e) => e.stopPropagation()} className="relative bg-background border-l-2 border-black w-full max-w-md h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#F3E8FF] border-b-2 border-black px-5 py-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><UserGear size={18} weight="bold" /> Permissions</h3>
          <button onClick={onClose} className="press w-8 h-8 rounded-full border-2 border-black bg-white flex items-center justify-center"><X size={14} weight="bold" /></button>
        </div>
        <div className="p-4 space-y-4">
          {Object.entries(byCat).map(([cat, perms]) => (
            <div key={cat}>
              <p className="text-xs font-bold uppercase tracking-wider text-black/40 mb-1.5">{cat}</p>
              <div className="space-y-1.5">
                {perms.map((p) => (
                  <div key={p.key} className="flex items-center justify-between gap-2 text-sm">
                    <span className={p.effective ? "" : "text-black/40"}>{p.name}</span>
                    <select data-testid={`perm-${p.key}`} value={p.override} onChange={(e) => setVal(p.key, e.target.value)} className="h-8 px-2 rounded-lg border-2 border-black bg-white text-xs font-bold">
                      <option value="inherited">Inherit</option>
                      <option value="allowed">Allow</option>
                      <option value="denied">Deny</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmployeeRow({ emp, roles, stores, depts, onChange }) {
  const [open, setOpen] = useState(false);
  const [permFor, setPermFor] = useState(null);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleStore = (sid) => {
    const cur = emp.store_ids.includes("*") ? [] : [...emp.store_ids];
    const next = cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid];
    act(() => api.put(`/employees/${emp.id}/stores`, { store_ids: next, access_level: "work" }), "Stores updated");
  };
  const setAll = () => act(() => api.put(`/employees/${emp.id}/stores`, { store_ids: ["*"], access_level: "full" }), "All-store access granted");
  return (
    <div data-testid={`employee-row-${emp.id}`} className="rounded-xl border-2 border-black bg-white overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-bold truncate">{emp.name}</p>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black capitalize" style={{ backgroundColor: STATUS_COLORS[emp.access_status] }}>{emp.access_status.replace(/_/g, " ")}</span>
          </div>
          <p className="text-xs text-black/50">{emp.role_name} · {emp.stores.join(", ") || "No store"} · AI {emp.ai_access ? "on" : "off"}</p>
        </div>
        <CaretDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t-2 border-black p-4 space-y-3 bg-[#FAFAFA]">
          <div className="grid grid-cols-2 gap-2 items-center">
            <label className="text-xs font-bold">Role</label>
            <select data-testid={`emp-role-${emp.id}`} value={emp.role} onChange={(e) => act(() => api.put(`/employees/${emp.id}/role`, { role: e.target.value }), "Role updated")} className={inputCls}>
              {roles.map((r) => <option key={r.role_type} value={r.role_type}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between"><label className="text-xs font-bold">Store access</label>
              <button data-testid={`emp-allstores-${emp.id}`} onClick={setAll} className="press text-[10px] font-bold px-2 py-1 rounded-full border-2 border-black bg-white">All stores</button></div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {stores.map((s) => {
                const on = emp.store_ids.includes("*") || emp.store_ids.includes(s.id);
                return <button key={s.id} data-testid={`emp-store-${emp.id}-${s.id}`} onClick={() => toggleStore(s.id)} className={`press text-xs font-bold px-2.5 py-1 rounded-full border-2 border-black ${on ? "bg-primary text-white" : "bg-white"}`}>{emp.store_ids.includes("*") ? s.name : (on ? <span className="flex items-center gap-1"><Check size={10} weight="bold" />{s.name}</span> : s.name)}</button>;
              })}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold">Departments</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {depts.map((d) => {
                const on = emp.department_ids.includes(d.id);
                const next = on ? emp.department_ids.filter((x) => x !== d.id) : [...emp.department_ids, d.id];
                return <button key={d.id} onClick={() => act(() => api.put(`/employees/${emp.id}/departments`, { department_ids: next }), "Departments updated")} className={`press text-xs font-bold px-2.5 py-1 rounded-full border-2 border-black ${on ? "bg-secondary text-white" : "bg-white"}`}>{d.name}</button>;
              })}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button data-testid={`emp-ai-${emp.id}`} onClick={() => act(() => api.post(`/employees/${emp.id}/ai`, { enabled: !emp.ai_access }), `AI ${emp.ai_access ? "disabled" : "enabled"}`)} className="press h-9 px-3 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center gap-1.5"><Sparkle size={13} weight="fill" /> {emp.ai_access ? "Disable AI" : "Enable AI"}</button>
            <button data-testid={`emp-perms-${emp.id}`} onClick={() => setPermFor(emp.id)} className="press h-9 px-3 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center gap-1.5"><UserGear size={13} weight="bold" /> Permissions</button>
            <button data-testid={`emp-signout-${emp.id}`} onClick={() => act(() => api.post(`/employees/${emp.id}/sign-out-all`), "Signed out of all devices")} className="press h-9 px-3 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center gap-1.5"><SignOut size={13} weight="bold" /> Sign out all</button>
          </div>
          <div className="grid grid-cols-2 gap-2 items-center">
            <label className="text-xs font-bold flex items-center gap-1"><Prohibit size={13} /> Access status</label>
            <select data-testid={`emp-status-${emp.id}`} value={emp.access_status} onChange={(e) => act(() => api.post(`/employees/${emp.id}/access-status`, { status: e.target.value }), "Access status updated")} className={inputCls}>
              {STATUS_OPTS.map((s) => <option key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          {emp.last_login && <p className="text-[11px] text-black/40">Last login: {new Date(emp.last_login).toLocaleString()}</p>}
        </div>
      )}
      {permFor && <PermEditor userId={permFor} onClose={() => { setPermFor(null); onChange(); }} />}
    </div>
  );
}

function StoresTab() {
  const [stores, setStores] = useState([]);
  const [depts, setDepts] = useState([]);
  const [sName, setSName] = useState(""); const [sNum, setSNum] = useState("");
  const [dName, setDName] = useState("");
  const load = useCallback(async () => {
    setStores((await api.get("/stores")).data); setDepts((await api.get("/departments")).data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const addStore = async () => { if (!sName.trim()) return; try { await api.post("/stores", { name: sName, store_number: sNum }); setSName(""); setSNum(""); load(); toast.success("Store added"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delStore = async (id) => { if (!window.confirm("Delete this store?")) return; try { await api.delete(`/stores/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const addDept = async () => { if (!dName.trim()) return; try { await api.post("/departments", { name: dName }); setDName(""); load(); toast.success("Department added"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delDept = async (id) => { try { await api.delete(`/departments/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-black/40 mb-2 flex items-center gap-1.5"><Storefront size={14} weight="bold" /> Stores</p>
        <div className="space-y-2">
          {stores.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 p-3 rounded-lg border-2 border-black bg-white text-sm">
              <span className="font-bold">{s.name} <span className="text-black/40 font-normal">#{s.store_number}</span></span>
              <button data-testid={`del-store-${s.id}`} onClick={() => delStore(s.id)} className="press w-7 h-7 rounded-full border-2 border-black bg-white flex items-center justify-center"><Trash size={12} weight="bold" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <input data-testid="store-name-input" className={inputCls} placeholder="Store name" value={sName} onChange={(e) => setSName(e.target.value)} />
          <input className={inputCls + " w-24"} placeholder="No." value={sNum} onChange={(e) => setSNum(e.target.value)} />
          <button data-testid="add-store-btn" onClick={addStore} className="press h-10 px-3 rounded-lg bg-primary text-white border-2 border-black font-bold flex items-center gap-1"><Plus size={16} weight="bold" /></button>
        </div>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-black/40 mb-2 flex items-center gap-1.5"><Buildings size={14} weight="bold" /> Departments</p>
        <div className="flex flex-wrap gap-1.5">
          {depts.map((d) => (
            <span key={d.id} className="text-xs font-bold px-2.5 py-1.5 rounded-full border-2 border-black bg-white flex items-center gap-1.5">{d.name}
              <button onClick={() => delDept(d.id)} className="press"><X size={11} weight="bold" /></button></span>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <input data-testid="dept-name-input" className={inputCls} placeholder="Department name" value={dName} onChange={(e) => setDName(e.target.value)} />
          <button data-testid="add-dept-btn" onClick={addDept} className="press h-10 px-3 rounded-lg bg-secondary text-white border-2 border-black font-bold flex items-center gap-1"><Plus size={16} weight="bold" /></button>
        </div>
      </div>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/access-logs").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-2" data-testid="audit-log">
      {logs.length === 0 && <p className="text-sm text-black/40">No activity logged yet.</p>}
      {logs.map((l) => (
        <div key={l.id} className="p-3 rounded-lg border-2 border-black bg-white text-xs flex items-center justify-between gap-2">
          <div><span className="font-bold">{l.user_name}</span> · {l.action} <span className="text-black/40">{l.resource_type}</span>
            {l.result === "denied" && <span className="ml-1 text-red-600 font-bold">DENIED</span>}</div>
          <span className="text-black/40 shrink-0">{new Date(l.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [cats, setCats] = useState({});
  const [emps, setEmps] = useState([]);
  const [editor, setEditor] = useState(null); // {id?, name, description, read_only, allowed:Set}
  const [applyFor, setApplyFor] = useState(null);
  const [applySel, setApplySel] = useState([]);
  const load = useCallback(async () => {
    setRoles((await api.get("/roles")).data);
    setCats((await api.get("/rbac/catalog")).data.categories);
    setEmps((await api.get("/employees")).data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditor({ name: "", description: "", read_only: false, allowed: new Set() });
  const openEdit = (r) => setEditor({ id: r.id, name: r.name, description: r.description || "", read_only: r.read_only, allowed: new Set(r.allowed_permissions), system: r.is_system_role, owner: r.role_type === "owner" });
  const toggleKey = (k) => setEditor((e) => { const s = new Set(e.allowed); s.has(k) ? s.delete(k) : s.add(k); return { ...e, allowed: s }; });
  const save = async () => {
    if (!editor.name.trim()) { toast.error("Name required"); return; }
    const body = { name: editor.name, description: editor.description, read_only: editor.read_only, allowed_permissions: [...editor.allowed] };
    try {
      if (editor.id) await api.put(`/roles/${editor.id}`, body);
      else await api.post("/roles", body);
      toast.success("Role saved"); setEditor(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const dup = async (r) => { try { await api.post(`/roles/${r.id}/duplicate`, { name: `${r.name} (copy)` }); toast.success("Duplicated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (r) => { if (!window.confirm(`Delete role ${r.name}?`)) return; try { await api.delete(`/roles/${r.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const doApply = async () => { try { const { data } = await api.post(`/roles/${applyFor.id}/apply`, { user_ids: applySel }); toast.success(`Applied to ${data.applied} employee(s)`); setApplyFor(null); setApplySel([]); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (editor) {
    return (
      <div data-testid="role-editor" className="space-y-4">
        <input data-testid="role-name-input" className={inputCls} placeholder="Role name" value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} disabled={editor.owner} />
        <input className={inputCls} placeholder="Description" value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })} />
        <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={editor.read_only} onChange={(e) => setEditor({ ...editor, read_only: e.target.checked })} className="w-4 h-4" /> Read-only role (blocks all writes)</label>
        {editor.owner && <p className="text-xs text-red-600 font-bold">The Owner role cannot be edited.</p>}
        <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
          {Object.entries(cats).map(([cat, perms]) => (
            <div key={cat}>
              <p className="text-xs font-bold uppercase tracking-wider text-black/40 mb-1">{cat}</p>
              <div className="grid grid-cols-2 gap-1.5">
                {perms.map((p) => (
                  <label key={p.key} className="flex items-center gap-1.5 text-xs font-bold">
                    <input type="checkbox" data-testid={`role-perm-${p.key}`} disabled={editor.owner} checked={editor.allowed.has(p.key)} onChange={() => toggleKey(p.key)} className="w-3.5 h-3.5" /> {p.name}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button data-testid="role-save-btn" onClick={save} disabled={editor.owner} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold border-2 border-black disabled:opacity-50">Save role</button>
          <button onClick={() => setEditor(null)} className="press flex-1 h-11 rounded-full bg-white font-bold border-2 border-black">Cancel</button>
        </div>
      </div>
    );
  }

  if (applyFor) {
    return (
      <div data-testid="role-apply" className="space-y-3">
        <p className="font-bold text-sm">Apply "{applyFor.name}" to employees</p>
        {emps.map((e) => (
          <label key={e.id} className="flex items-center gap-2 p-2.5 rounded-lg border-2 border-black bg-white text-sm font-bold">
            <input type="checkbox" checked={applySel.includes(e.id)} onChange={(ev) => setApplySel((s) => ev.target.checked ? [...s, e.id] : s.filter((x) => x !== e.id))} className="w-4 h-4" /> {e.name} <span className="text-black/40 font-normal">({e.role_name})</span>
          </label>
        ))}
        <div className="flex gap-2">
          <button data-testid="role-apply-confirm" onClick={doApply} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold border-2 border-black">Apply</button>
          <button onClick={() => { setApplyFor(null); setApplySel([]); }} className="press flex-1 h-11 rounded-full bg-white font-bold border-2 border-black">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button data-testid="role-new-btn" onClick={openNew} className="press w-full h-11 rounded-full bg-primary text-white font-bold border-2 border-black flex items-center justify-center gap-2"><Plus size={16} weight="bold" /> New custom role / template</button>
      {roles.map((r) => (
        <div key={r.id} data-testid={`role-item-${r.id}`} className="rounded-xl border-2 border-black bg-white p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="font-bold flex items-center gap-2">{r.name}
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black" style={{ backgroundColor: r.is_system_role ? "#E0F2FE" : "#F3E8FF" }}>{r.is_system_role ? "system" : "custom"}</span>
                {r.read_only && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-[#FEF9C3]">read-only</span>}</p>
              <p className="text-xs text-black/50">{r.allowed_permissions.length} permissions</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <button data-testid={`role-edit-${r.id}`} onClick={() => openEdit(r)} className="press h-8 px-3 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center gap-1"><PencilSimple size={12} weight="bold" /> Edit</button>
            <button onClick={() => dup(r)} className="press h-8 px-3 rounded-full border-2 border-black bg-white text-xs font-bold">Duplicate</button>
            <button data-testid={`role-apply-${r.id}`} onClick={() => setApplyFor(r)} className="press h-8 px-3 rounded-full border-2 border-black bg-white text-xs font-bold">Apply to…</button>
            {!r.is_system_role && <button onClick={() => del(r)} className="press h-8 px-3 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center gap-1"><Trash size={12} weight="bold" /></button>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function EmployeesAccess({ open, onClose }) {
  const { can } = useAuth();
  const [tab, setTab] = useState("employees");
  const [emps, setEmps] = useState([]);
  const [roles, setRoles] = useState([]);
  const [stores, setStores] = useState([]);
  const [depts, setDepts] = useState([]);
  const load = useCallback(async () => {
    try {
      setEmps((await api.get("/employees")).data);
      setStores((await api.get("/stores")).data);
      setDepts((await api.get("/departments")).data);
      if (can("permission_management")) setRoles((await api.get("/roles")).data);
    } catch { /* ignore */ }
  }, [can]);
  useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;
  const tabs = [["employees", "Employees"], ["roles", "Roles"], ["stores", "Stores & Depts"], ["audit", "Audit Log"]];
  return (
    <div className="fixed inset-0 z-[75] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid="employees-access-panel" onClick={(e) => e.stopPropagation()} className="relative bg-background border-l-2 border-black w-full max-w-xl h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#E0F2FE] border-b-2 border-black px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-secondary border-2 border-black flex items-center justify-center"><ShieldCheck size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-xl font-bold">Employees & Access</h2>
          </div>
          <button data-testid="ea-close-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white"><X size={16} weight="bold" /></button>
        </div>
        <div className="sticky top-[65px] bg-background z-10 px-5 pt-4 flex gap-2 border-b-2 border-black pb-3">
          {tabs.map(([k, label]) => (
            <button key={k} data-testid={`ea-tab-${k}`} onClick={() => setTab(k)} className={`press h-9 px-4 rounded-full text-sm font-bold border-2 border-black ${tab === k ? "bg-secondary text-white" : "bg-white"}`}>{label}</button>
          ))}
        </div>
        <div className="p-5">
          {tab === "employees" && (
            <div className="space-y-2">
              {emps.map((e) => <EmployeeRow key={e.id} emp={e} roles={roles} stores={stores} depts={depts} onChange={load} />)}
            </div>
          )}
          {tab === "stores" && <StoresTab />}
          {tab === "roles" && <RolesTab />}
          {tab === "audit" && <AuditTab />}
        </div>
      </div>
    </div>
  );
}
